import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  PaymentMethodData, 
  GetPaymentMethodsResponse, 
  PaymentMethodFilters, 
  PaymentMethodForm, 
  PaymentMethodUpdateForm,
  PaymentMethodReorderRequest
} from '../../types';
import apiService from '../../services/api';

interface PaymentMethodsState {
  paymentMethods: PaymentMethodData[];
  selectedPaymentMethod: PaymentMethodData | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: PaymentMethodFilters;
  loading: boolean;
  error: string | null;
}

const initialState: PaymentMethodsState = {
  paymentMethods: [],
  selectedPaymentMethod: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
  filters: {},
  loading: false,
  error: null,
};

export const fetchPaymentMethods = createAsyncThunk(
  'paymentMethods/fetchPaymentMethods',
  async ({ page, limit, filters }: { page: number; limit: number; filters?: PaymentMethodFilters }) => {
    const response = await apiService.getPaymentMethods(page, limit, filters);
    return response;
  }
);

export const fetchPaymentMethodDetails = createAsyncThunk(
  'paymentMethods/fetchPaymentMethodDetails',
  async (methodId: string) => {
    const response = await apiService.getPaymentMethod(methodId);
    return response;
  }
);

export const createPaymentMethod = createAsyncThunk(
  'paymentMethods/createPaymentMethod',
  async (methodData: PaymentMethodForm) => {
    const response = await apiService.createPaymentMethod(methodData);
    return response;
  }
);

export const updatePaymentMethod = createAsyncThunk(
  'paymentMethods/updatePaymentMethod',
  async ({ methodId, methodData }: { methodId: string; methodData: PaymentMethodUpdateForm }) => {
    const response = await apiService.updatePaymentMethod(methodId, methodData);
    return response;
  }
);

export const togglePaymentMethodStatus = createAsyncThunk(
  'paymentMethods/togglePaymentMethodStatus',
  async (methodId: string) => {
    const response = await apiService.togglePaymentMethodStatus(methodId);
    return { methodId, response };
  }
);

export const deletePaymentMethod = createAsyncThunk(
  'paymentMethods/deletePaymentMethod',
  async (methodId: string) => {
    const response = await apiService.deletePaymentMethod(methodId);
    return { methodId, response };
  }
);

export const reorderPaymentMethods = createAsyncThunk(
  'paymentMethods/reorderPaymentMethods',
  async (reorderData: PaymentMethodReorderRequest) => {
    const response = await apiService.reorderPaymentMethods(reorderData);
    return response;
  }
);

const paymentMethodsSlice = createSlice({
  name: 'paymentMethods',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<PaymentMethodFilters>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setSelectedPaymentMethod: (state, action: PayloadAction<PaymentMethodData | null>) => {
      state.selectedPaymentMethod = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch payment methods
      .addCase(fetchPaymentMethods.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentMethods.fulfilled, (state, action) => {
        state.loading = false;
        const response = action.payload as GetPaymentMethodsResponse;
        if (response.success && response.data) {
          state.paymentMethods = response.data;
          if (response.pagination) {
            state.pagination = {
              page: response.pagination.page,
              limit: response.pagination.limit,
              total: response.pagination.total,
              totalPages: response.pagination.pages,
            };
          }
        }
      })
      .addCase(fetchPaymentMethods.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch payment methods';
      })
      // Fetch payment method details
      .addCase(fetchPaymentMethodDetails.fulfilled, (state, action) => {
        state.selectedPaymentMethod = action.payload;
      })
      // Create payment method
      .addCase(createPaymentMethod.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPaymentMethod.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentMethods.unshift(action.payload);
      })
      .addCase(createPaymentMethod.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create payment method';
      })
      // Update payment method
      .addCase(updatePaymentMethod.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePaymentMethod.fulfilled, (state, action) => {
        state.loading = false;
        const updatedMethod = action.payload;
        const index = state.paymentMethods.findIndex(method => method.id === updatedMethod.id);
        if (index !== -1) {
          state.paymentMethods[index] = updatedMethod;
        }
        if (state.selectedPaymentMethod?.id === updatedMethod.id) {
          state.selectedPaymentMethod = updatedMethod;
        }
      })
      .addCase(updatePaymentMethod.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update payment method';
      })
      // Toggle payment method status
      .addCase(togglePaymentMethodStatus.fulfilled, (state, action) => {
        const { methodId } = action.payload;
        const method = state.paymentMethods.find(m => m.id === methodId);
        if (method) {
          method.isActive = !method.isActive;
        }
        if (state.selectedPaymentMethod?.id === methodId) {
          state.selectedPaymentMethod.isActive = !state.selectedPaymentMethod.isActive;
        }
      })
      // Delete payment method
      .addCase(deletePaymentMethod.fulfilled, (state, action) => {
        const { methodId } = action.payload;
        state.paymentMethods = state.paymentMethods.filter(method => method.id !== methodId);
        if (state.selectedPaymentMethod?.id === methodId) {
          state.selectedPaymentMethod = null;
        }
      })
      // Reorder payment methods
      .addCase(reorderPaymentMethods.fulfilled, (state, action) => {
        // Refresh the list to get the new order
        // The actual reordering will be handled by the API
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setPage,
  setSelectedPaymentMethod,
  clearError,
} = paymentMethodsSlice.actions;

export default paymentMethodsSlice.reducer; 